import React, { useState, useEffect } from 'react'
import { softwareApi } from '../services/adminApi'
import {
  CommandLineIcon,
  ArrowUpTrayIcon,
  TrashIcon,
  PencilIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline'

interface SoftwareTool {
  id: number
  software_name: string
  software_name_en?: string
  version: string
  platform: string
  download_url: string
  file_size?: number
  release_notes?: string
  release_notes_en?: string
  is_latest: boolean
  is_required: boolean
  created_at: string
}

const SoftwareToolsManagement: React.FC = () => {
  const [tools, setTools] = useState<SoftwareTool[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [formData, setFormData] = useState({
    software_name: '',
    software_name_en: '',
    version: '',
    platform: 'windows',
    download_url: '',
    file_size: 0,
    release_notes: '',
    release_notes_en: '',
    is_latest: true,
    is_required: false
  })

  useEffect(() => {
    fetchTools()
  }, [])

  const fetchTools = async () => {
    try {
      setLoading(true)
      const response = await softwareApi.list()
      if (response.success && response.data) {
        setTools(response.data)
      } else {
        setTools(response.data as unknown as SoftwareTool[])
      }
    } catch (err) {
      console.error('Failed to fetch tools:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      // Clear previous download URL if user selects a new file
      setFormData(prev => ({ ...prev, download_url: '' }))
    }
  }

  const uploadFile = async (file: File): Promise<string | null> => {
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('type', 'software')
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/upload`, {
        method: 'POST',
        headers: {
           'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: form
      })
      
      const data = await response.json()
      if (data.success) {
        return data.data.url
      } else {
        throw new Error(data.error?.message || '未知错误')
      }
    } catch (err: any) {
      console.error('Upload failed:', err)
      alert('文件上传失败: ' + err.message)
      return null
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      software_name: '',
      software_name_en: '',
      version: '',
      platform: 'windows',
      download_url: '',
      file_size: 0,
      release_notes: '',
      release_notes_en: '',
      is_latest: true,
      is_required: false
    })
    setSelectedFile(null)
    setEditingId(null)
  }

  const handleOpenModal = () => {
    resetForm()
    setShowModal(true)
  }

  const handleEdit = (tool: SoftwareTool) => {
    setFormData({
      software_name: tool.software_name,
      software_name_en: tool.software_name_en || '',
      version: tool.version,
      platform: tool.platform,
      download_url: tool.download_url,
      file_size: tool.file_size || 0,
      release_notes: tool.release_notes || '',
      release_notes_en: tool.release_notes_en || '',
      is_latest: tool.is_latest,
      is_required: tool.is_required
    })
    setEditingId(tool.id)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    resetForm()
    setShowModal(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    let finalDownloadUrl = formData.download_url
    let finalFileSize = formData.file_size

    // If file is selected, upload it first
    if (selectedFile) {
      const uploadedUrl = await uploadFile(selectedFile)
      if (!uploadedUrl) return // Stop if upload failed
      finalDownloadUrl = uploadedUrl
      finalFileSize = selectedFile.size
    }

    try {
      let response;
      if (editingId) {
        response = await softwareApi.update(editingId, {
          ...formData,
          download_url: finalDownloadUrl,
          file_size: finalFileSize
        })
      } else {
        response = await softwareApi.upload({
          ...formData,
          download_url: finalDownloadUrl,
          file_size: finalFileSize
        })
      }

      if (response.success) {
        handleCloseModal()
        fetchTools()
      } else {
        alert(editingId ? '更新失败' : '发布失败')
      }
    } catch (err) {
      console.error('Failed to save tool:', err)
      alert(editingId ? '更新出错' : '发布出错')
    }
  }

  const handleDelete = async (id: number) => {
    if (!window.confirm('确定要删除这个工具吗？')) return
    try {
      const response = await softwareApi.delete(id)
      if (response.success) {
        fetchTools()
      } else {
        alert('删除失败')
      }
    } catch (err) {
      console.error('Failed to delete tool:', err)
      alert('删除出错')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">软件工具管理</h2>
          <p className="mt-1 text-sm text-gray-500">管理学生和教师端的软件下载列表</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
          发布新工具
        </button>
      </div>

      {/* Tools Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">软件名称</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">版本</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">平台</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">发布时间</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tools.map((tool) => (
              <tr key={tool.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {tool.software_name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tool.version}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {tool.platform}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(tool.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {tool.is_latest && (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      最新
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button 
                    className="text-blue-600 hover:text-blue-900 mr-4"
                    onClick={() => handleEdit(tool)}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button 
                    className="text-red-600 hover:text-red-900"
                    onClick={() => handleDelete(tool.id)}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {tools.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                  暂无数据
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Upload Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">发布新工具</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">软件名称（中文）<span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={formData.software_name}
                  onChange={(e) => setFormData({ ...formData, software_name: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="例如：腾讯会议"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">软件名称（英文）</label>
                <input
                  type="text"
                  value={formData.software_name_en}
                  onChange={(e) => setFormData({ ...formData, software_name_en: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="e.g., Tencent Meeting"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">版本号</label>
                  <input
                    type="text"
                    required
                    value={formData.version}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">平台</label>
                  <select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="windows">Windows</option>
                    <option value="macos">macOS</option>
                    <option value="ios">iOS</option>
                    <option value="android">Android</option>
                  </select>
                </div>
              </div>

              {/* File Upload Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700">上传安装包</label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>上传文件</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          className="sr-only"
                          onChange={handleFileSelect}
                        />
                      </label>
                      <p className="pl-1">或拖拽文件到此处</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      {selectedFile ? `已选择: ${selectedFile.name} (${(selectedFile.size / 1024 / 1024).toFixed(2)} MB)` : '支持 .exe, .dmg, .pkg, .zip, .apk (最大 500MB)'}
                    </p>
                  </div>
                </div>
                {uploading && (
                  <p className="mt-2 text-sm text-blue-600">正在上传中，请稍候...</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">下载链接 (自动生成或手动填写)</label>
                <input
                  type="url"
                  required
                  value={formData.download_url}
                  onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-gray-50"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">更新说明（中文）</label>
                <textarea
                  rows={3}
                  value={formData.release_notes}
                  onChange={(e) => setFormData({ ...formData, release_notes: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="请输入中文更新说明"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">更新说明（英文）</label>
                <textarea
                  rows={3}
                  value={formData.release_notes_en}
                  onChange={(e) => setFormData({ ...formData, release_notes_en: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Please enter English release notes"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_latest"
                  checked={formData.is_latest}
                  onChange={(e) => setFormData({ ...formData, is_latest: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_latest" className="ml-2 block text-sm text-gray-900">
                  标记为最新版本
                </label>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {uploading ? '上传中...' : '发布'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default SoftwareToolsManagement
